/**
 * useChatRenderState — the pre-return derived-render block lifted out of ChatBox
 * (delegation pattern). The derivations + useMemos were originally reproduced
 * VERBATIM from the ChatBox block — modulePanePlacement, isChatBusy/isEmptyChat,
 * shouldShowThinkingTimeline, thinkingTimelineSteps, selectedChatPaneModuleTitle,
 * the modulePaneElement useMemo (with its EXACT dep array), and
 * modulePanePortalTarget — with ONE intentional behavior change since: the
 * shouldShowThinkingTimeline gate now keeps the status pill mounted for the whole
 * streaming turn (so it never blanks mid-turn) rather than hiding it once
 * assistant text starts streaming.
 *
 * This is a CONTIGUOUS slice of plain derivations + two useMemos, so calling it
 * at the same position the block occupied preserves hook order exactly. It is a
 * .tsx because modulePaneElement renders <ChatModulePane> — the quarantine
 * sub-locus for pane-only @/modules imports. That import already lived in
 * ChatBox and ChatModulePane stays under features/chat, so re-homing the memo
 * here surfaces NO new cross-layer import site. State is owned by the
 * orchestrator and threaded in; refs thread in by reference.
 */
import { useMemo } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { ChatModulePane } from '../components/chatbox/ChatModulePane';
import { STATUS_MESSAGES } from '../components/chatbox/constants';
import type { ModulePanePlacement } from '../components/chatbox/types';
import type { ChatMessage } from '@/features/chat/hooks/useChat';
import type { StreamActivityStep } from '@/features/chat/hooks/useChat.types';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import { getModuleMeta, normalizeStaleModuleLabel } from '@/types/modules';
import type { EditableModuleTitleRef } from '@/modules/_infrastructure/EditableModuleTitle';

export interface UseChatRenderStateDeps {
  shouldShowAnyPane: boolean;
  isStreaming: boolean;
  isLocalViewIntentThinking: boolean;
  isLoading: boolean;
  messages: ChatMessage[];
  streamActivity: StreamActivityStep[];
  thinking: string | null;
  elapsedSeconds: number;
  selectedChatPaneModule: Module | null;
  shouldShowModulePane: boolean;
  activeConversation: Conversation | null;
  isMobile: boolean;
  isModulePaneFullscreenActive: boolean;
  hideModulePane: () => void;
  moduleMenuCloseSignal: number;
  showIntegrationAccountsModal: boolean;
  showManageAccountsModal: boolean;
  setShowIntegrationAccountsModal: Dispatch<SetStateAction<boolean>>;
  setShowManageAccountsModal: Dispatch<SetStateAction<boolean>>;
  modulePaneCardRef: RefObject<HTMLDivElement | null>;
  modulePaneTitleRef: RefObject<EditableModuleTitleRef | null>;
  chatBoxRootRef: RefObject<HTMLDivElement | null>;
}

export interface UseChatRenderStateResult {
  modulePanePlacement: ModulePanePlacement;
  isChatBusy: boolean;
  isEmptyChat: boolean;
  /** isEmptyChat, but a lone welcome greeting still counts as "pre-engagement"
   *  (so the new-chat connect affordance survives the greeting). */
  isPreEngagementChat: boolean;
  shouldShowThinkingTimeline: boolean;
  thinkingTimelineSteps: StreamActivityStep[];
  selectedChatPaneModuleTitle: string;
  modulePaneElement: React.ReactElement | null;
  modulePanePortalTarget: HTMLDivElement | null;
}

/**
 * Pure derivation of the two empty-chat flags. Extracted so the greeting↔connect
 * interaction is unit-testable without the hook's heavy dep object.
 *
 * - `isEmptyChat` — no real messages at all (drives the centered empty layout +
 *   scroll suppression). A welcome greeting IS a real message, so a greeting-only
 *   thread is NOT empty (preserves the historical, non-centered layout).
 * - `isPreEngagementChat` — the user hasn't engaged yet: the thread is empty OR
 *   holds only the returning-user welcome greeting (assistant, routing:'greeting').
 *   Used to keep the new-chat connect affordance visible alongside the greeting.
 *   Any non-greeting real message (a user turn or assistant reply) flips it false.
 */
export function deriveEmptyChatFlags(
  messages: Pick<ChatMessage, 'inlineModuleId' | 'role' | 'routing'>[],
  isChatBusy: boolean,
  shouldSuppressEmptyStateForModule: boolean,
): { isEmptyChat: boolean; isPreEngagementChat: boolean } {
  const hasRealMessages = messages.some((m) => !m.inlineModuleId);
  const hasNonGreetingRealMessage = messages.some(
    (m) => !m.inlineModuleId && !(m.role === 'assistant' && m.routing === 'greeting'),
  );
  const settled = !isChatBusy && !shouldSuppressEmptyStateForModule;
  return {
    isEmptyChat: !hasRealMessages && settled,
    isPreEngagementChat: !hasNonGreetingRealMessage && settled,
  };
}

export function useChatRenderState(deps: UseChatRenderStateDeps): UseChatRenderStateResult {
  const {
    shouldShowAnyPane,
    isStreaming,
    isLocalViewIntentThinking,
    isLoading,
    messages,
    streamActivity,
    thinking,
    elapsedSeconds,
    selectedChatPaneModule,
    shouldShowModulePane,
    activeConversation,
    isMobile,
    isModulePaneFullscreenActive,
    hideModulePane,
    moduleMenuCloseSignal,
    showIntegrationAccountsModal,
    showManageAccountsModal,
    setShowIntegrationAccountsModal,
    setShowManageAccountsModal,
    modulePaneCardRef,
    modulePaneTitleRef,
    chatBoxRootRef,
  } = deps;

  const modulePanePlacement: ModulePanePlacement = !shouldShowAnyPane
    ? 'hidden'
    : 'split';
  const isChatBusy = isStreaming || isLocalViewIntentThinking || isLoading;
  const shouldSuppressEmptyStateForModule = modulePanePlacement !== 'hidden';
  const { isEmptyChat, isPreEngagementChat } = deriveEmptyChatFlags(
    messages,
    isChatBusy,
    shouldSuppressEmptyStateForModule,
  );
  // While a turn is streaming, keep the status pill mounted unconditionally — it
  // must NEVER blank mid-turn while the agent is still working. The rendered
  // label always resolves to a concrete status, the last `thinking` value, or a
  // generic "Thinking..." fallback (see thinkingTimelineSteps below), so the pill
  // is never empty. Previously this hid the pill once assistant text was
  // streaming (unless a stall/concrete step kept it up), which flickered the pill
  // away in the gap between phases of a multi-step turn.
  const shouldShowThinkingTimeline = isStreaming || isLocalViewIntentThinking;
  // Single-line status. The hook still appends concrete work statuses to
  // streamActivity (so phrase-dedup + min-display-time both work), but
  // the UI only renders the most recent step — no stacked history, no
  // bulleted timeline. The current step's label animates via
  // AnimatedThinkingLabel downstream.
  // Memoized so the array identity only changes when the rendered step actually
  // changes — a fresh array per render would defeat MessageList's memo (#5255).
  const thinkingTimelineSteps = useMemo(() => (
    isStreaming && streamActivity.length > 0
      ? [streamActivity[streamActivity.length - 1]]
      : [{
        id: isStreaming ? 'fallback' : 'local-fallback',
        label: isStreaming
          ? thinking || STATUS_MESSAGES[elapsedSeconds % STATUS_MESSAGES.length]
          : STATUS_MESSAGES[0],
        type: 'thinking' as const,
        timestamp: Date.now(),
      }]
  ), [isStreaming, streamActivity, thinking, elapsedSeconds]);
  const selectedChatPaneModuleTitle = useMemo(() => {
    if (!selectedChatPaneModule) return '';
    // Drop a stale pre-rename default label (e.g. task_list's old "Tasks") so it
    // resolves to the current catalog default (Kanban) rather than the old name.
    const normalizedLabel = normalizeStaleModuleLabel(selectedChatPaneModule.type, selectedChatPaneModule.label);
    if (normalizedLabel && normalizedLabel.trim()) {
      return normalizedLabel.trim();
    }
    const metaDefault = getModuleMeta(selectedChatPaneModule.type)?.defaultLabel;
    if (metaDefault) return metaDefault;
    const fallback = selectedChatPaneModule.type || 'Module';
    return fallback
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, [selectedChatPaneModule]);
  const modulePaneElement = useMemo(() => {
    if (!activeConversation?.id) return null;

    if (!shouldShowModulePane || !selectedChatPaneModule) return null;

    // QUARANTINE: all pane-only `@/modules` imports live in ChatModulePane.
    // Rendered inside this useMemo so memoization / render timing stays
    // unchanged. The orchestrator keeps every piece of state and threads it
    // as props.
    return (
      <ChatModulePane
        selectedChatPaneModule={selectedChatPaneModule}
        activeConversationId={activeConversation.id}
        isMobile={isMobile}
        modulePaneCardRef={modulePaneCardRef}
        modulePaneTitleRef={modulePaneTitleRef}
        selectedChatPaneModuleTitle={selectedChatPaneModuleTitle}
        hideModulePane={hideModulePane}
        moduleMenuCloseSignal={moduleMenuCloseSignal}
        showIntegrationAccountsModal={showIntegrationAccountsModal}
        showManageAccountsModal={showManageAccountsModal}
        setShowIntegrationAccountsModal={setShowIntegrationAccountsModal}
        setShowManageAccountsModal={setShowManageAccountsModal}
      />
    );
  }, [
    activeConversation?.id,
    hideModulePane,
    isMobile,
    isModulePaneFullscreenActive,
    showIntegrationAccountsModal,
    showManageAccountsModal,
    selectedChatPaneModule,
    shouldShowModulePane,
    selectedChatPaneModuleTitle,
  ]);
  const modulePanePortalTarget = modulePanePlacement === 'split'
    ? chatBoxRootRef.current
    : null;

  return {
    modulePanePlacement,
    isChatBusy,
    isEmptyChat,
    isPreEngagementChat,
    shouldShowThinkingTimeline,
    thinkingTimelineSteps,
    selectedChatPaneModuleTitle,
    modulePaneElement,
    modulePanePortalTarget,
  };
}
